import { FIELD_MISSION_START_HOLD_MS, parseEngineHours } from '@groupe-reca/contracts'
import { useQueryClient } from '@tanstack/react-query'
import {
  Handshake,
  Home,
  LocateFixed,
  LocateOff,
  LogOut,
  MapPin,
  Play,
  RefreshCw,
  Tractor,
  TriangleAlert,
  User,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { appDependencies } from '@/app/dependencies'
import logo from '@/assets/logo.svg'
import { useAuth } from '@/features/auth'
import { runCommand } from '../application/commands'
import { missionKeys, useHome, useRoutePlan } from '../application/missionQueries'
import { operationKeys, useReinforcementOffers } from '../application/operationsQueries'
import { useOperator } from '../application/OperatorServices'
import { blockerMessages, engineHoursErrors, formatDateTime, formatEngineHours, serverMessage } from '../domain/fieldText'
import { HelpAvailability } from './components/HelpAvailability'
import { HelpButton } from './components/HelpButton'
import { HoldButton } from './components/HoldButton'
import { ReinforcementPanel } from './components/ReinforcementPanel'
import { RoutePlanList } from './components/RoutePlanList'
import { chosenRoute, departureBlockers, routeActionLabel, routeResidences } from './routePlan'

type GpsPermission = 'granted' | 'denied' | 'prompt' | 'unknown'

/** Autorisation de localisation, lue sans la demander (la demande vient au démarrage de la mission). */
function useGpsPermission(): GpsPermission {
  const [permission, setPermission] = useState<GpsPermission>('unknown')
  useEffect(() => {
    let cancelled = false
    void appDependencies.geolocation.permission().then((value) => {
      if (!cancelled) setPermission(value)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return permission
}

function today(): string {
  return new Intl.DateTimeFormat('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date())
}

/**
 * Écran de départ « Prêt à partir » (maquettes) : opérateur, tracteur assigné, routes dans l'ordre du bureau (la
 * route suggérée par le serveur est présélectionnée, l'opérateur peut en choisir une autre admissible), résidences, compteur d'heures moteur au départ, démarrage par maintien de 2 s (ou confirmation, alternative
 * accessible). Une mission déjà ouverte se reprend, sans nouveau relevé. Sans mission : disponibilité pour aider et
 * renfort (mission distincte dans l'opération ouverte).
 */
export function StartPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { userId, repository, store, syncState, disconnectPresence } = useOperator()
  const home = useHome()
  const routePlan = useRoutePlan()
  const offers = useReinforcementOffers()
  const gpsPermission = useGpsPermission()
  const [hours, setHours] = useState('')
  const [touched, setTouched] = useState(false)
  const [pending, setPending] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [confirmSignOut, setConfirmSignOut] = useState(false)
  const [confirmStart, setConfirmStart] = useState(false)
  const [reinforcementHours, setReinforcementHours] = useState('')
  const [reinforcementPending, setReinforcementPending] = useState(false)
  const [reinforcementMessage, setReinforcementMessage] = useState<string | null>(null)
  const [pickedRouteId, setPickedRouteId] = useState<string | null>(null)

  const parsed = parseEngineHours(hours)
  const data = home.data
  const lastReading = data?.equipment?.lastReading ?? null
  const belowLast = parsed.ok && lastReading !== null && parsed.value < lastReading.value
  const plan = routePlan.data
  const hasPlan = (plan?.routes.length ?? 0) > 0
  const route = chosenRoute(plan, pickedRouteId)
  const blockers = data ? departureBlockers(data.blockers, plan) : []
  // Avec un plan, aucune route admissible bloque le départ (route principale comprise).
  const noRouteToStart = hasPlan && route === null
  const blocked = blockers.length > 0 || noRouteToStart
  // Plan en lecture : attendre, sinon le départ partirait sur la route principale sans choix.
  const startDisabled = blocked || routePlan.isPending
  const pendingPoints = syncState?.pending ?? 0
  const detectionAvailable = appDependencies.detection.available
  const operation = data?.operation ?? null
  const activeOffers = offers.data?.offers.filter((offer) => offer.status === 'ACTIVE').length ?? 0
  const reinforcementParsed = parseEngineHours(reinforcementHours)

  async function signOut() {
    await disconnectPresence()
    await auth.signOut()
  }

  /** Validation avant tout maintien : rien ne part vers le serveur avec un relevé invalide. */
  function canStart(): boolean {
    setTouched(true)
    setMessage(null)
    return parsed.ok && !startDisabled && !belowLast
  }

  async function start() {
    setConfirmStart(false)
    if (!canStart() || !parsed.ok) return
    setPending(true)
    // Route et passage lus dans le plan : le serveur refuse un passage changé entre-temps (MISSION_PASS_CHANGED).
    const choice = route ? { routeId: route.routeId, expectedPassNumber: route.passNumber } : {}
    const outcome = await runCommand(store, 'start', (idempotencyKey) =>
      repository.start({ idempotencyKey, startEngineHours: parsed.normalized, ...choice }),
    )
    setPending(false)
    if (outcome.kind === 'ok') {
      // Les offres dépendent de la mission ouverte (`canClaim`) : relues avec elle.
      await queryClient.invalidateQueries({ queryKey: missionKeys.home(userId) })
      await queryClient.invalidateQueries({ queryKey: operationKeys.offers(userId) })
      void navigate(`/mission/${outcome.value.missionId}`)
      return
    }
    if (outcome.kind === 'refused') {
      setMessage(serverMessage(outcome.code))
      await Promise.all([home.refetch(), routePlan.refetch()])
      return
    }
    // Résultat inconnu : relire avant de décider. Si la mission existe, on la reprend; sinon le même appui renverra
    // la même demande (même clé), jamais une seconde mission.
    const fresh = await home.refetch()
    const opened = fresh.data?.openMission
    if (opened) {
      await store.clearKey('start')
      void navigate(`/mission/${opened.id}`)
      return
    }
    setMessage('Le serveur n’a pas répondu. Vérifiez le réseau puis appuyez de nouveau : la même demande sera renvoyée.')
  }

  /** Renfort : mission distincte (REINFORCEMENT) dans l'opération ouverte, équipement et compteur exigés. */
  async function startReinforcement() {
    setReinforcementMessage(null)
    if (!reinforcementParsed.ok) {
      setReinforcementMessage(engineHoursErrors[reinforcementParsed.reason])
      return
    }
    setReinforcementPending(true)
    const outcome = await runCommand(store, 'start-reinforcement', (idempotencyKey) =>
      repository.startReinforcement({ idempotencyKey, startEngineHours: reinforcementParsed.normalized }),
    )
    setReinforcementPending(false)
    if (outcome.kind === 'ok') {
      // Les offres dépendent de la mission ouverte (`canClaim`) : relues avec elle.
      await queryClient.invalidateQueries({ queryKey: missionKeys.home(userId) })
      await queryClient.invalidateQueries({ queryKey: operationKeys.offers(userId) })
      void navigate(`/mission/${outcome.value.missionId}`)
      return
    }
    if (outcome.kind === 'refused') {
      setReinforcementMessage(serverMessage(outcome.code))
      return
    }
    const fresh = await home.refetch()
    const opened = fresh.data?.openMission
    if (opened) {
      await store.clearKey('start-reinforcement')
      void navigate(`/mission/${opened.id}`)
      return
    }
    setReinforcementMessage('Le serveur n’a pas répondu. Réessayez : la même demande sera renvoyée.')
  }

  return (
    <main className="op-screen">
      <header className="op-topbar">
        <img src={logo} alt="Groupe RÉCA" className="op-topbar__logo" />
        <span className="op-topbar__name">{data?.operator.name ?? auth.session?.displayName}</span>
        <span className="op-topbar__spacer" />
        {gpsPermission === 'granted' ? (
          <span className="field-pill field-pill--ok" role="status">
            <LocateFixed aria-hidden="true" /> GPS autorisé
          </span>
        ) : gpsPermission === 'denied' ? (
          <span className="field-pill field-pill--warn" role="status">
            <LocateOff aria-hidden="true" /> GPS refusé
          </span>
        ) : gpsPermission === 'prompt' ? (
          <span className="field-pill" role="status">
            <LocateFixed aria-hidden="true" /> GPS demandé au départ
          </span>
        ) : null}
        {pendingPoints > 0 ? (
          <span className="field-pill field-pill--warn" role="status">
            {pendingPoints} position{pendingPoints > 1 ? 's' : ''} en attente d’envoi
          </span>
        ) : null}
        <HelpButton />
        <button
          type="button"
          className="field-button field-button--outline"
          onClick={() => {
            if (pendingPoints > 0) setConfirmSignOut(true)
            else void signOut()
          }}
        >
          <LogOut aria-hidden="true" /> Se déconnecter
        </button>
      </header>

      {confirmSignOut ? (
        <div className="op-banner op-banner--warn" role="alert">
          <p>
            {pendingPoints} position{pendingPoints > 1 ? 's' : ''} n’ont pas encore été transmises. Elles restent sur
            cette tablette, dans l’espace de votre compte, et partiront à votre prochaine connexion.
          </p>
          <button type="button" className="field-button field-button--outline" onClick={() => void signOut()}>
            Se déconnecter quand même
          </button>
          <button
            type="button"
            className="field-button field-button--primary"
            onClick={() => {
              setConfirmSignOut(false)
            }}
          >
            Rester connecté
          </button>
        </div>
      ) : null}

      <div className="op-start">
        {home.isPending ? <p className="op-muted">Chargement de vos affectations…</p> : null}
        {home.isError ? (
          <div className="field-error" role="alert">
            <strong>Vos affectations n’ont pas pu être chargées</strong>
            <p>Le serveur n’a pas répondu. Vérifiez le réseau.</p>
            <button type="button" className="field-button field-button--primary" onClick={() => void home.refetch()}>
              <RefreshCw aria-hidden="true" /> Réessayer
            </button>
          </div>
        ) : null}

        {data?.openMission ? (
          <section className="op-card op-resume" aria-label="Mission en cours">
            <span className="field-eyebrow">
              Mission n° {data.openMission.number ?? '—'} · {data.openMission.status === 'PAUSED' ? 'en pause' : 'en cours'}
            </span>
            <h1>
              {data.openMission.routeName} — {data.openMission.equipmentLabel}
            </h1>
            <p className="op-muted">
              Démarrée le {formatDateTime(data.openMission.startedAt)} ·{' '}
              {data.openMission.progress.completed}/{data.openMission.progress.total} résidences terminées
            </p>
            <button
              type="button"
              className="op-start-button"
              onClick={() => void navigate(`/mission/${data.openMission?.id ?? ''}`)}
            >
              <Play aria-hidden="true" /> Reprendre ma mission
            </button>
          </section>
        ) : null}

        {data && !data.openMission ? (
          <form
            className="op-departure2"
            onSubmit={(event) => {
              // Entrée au clavier : jamais un démarrage direct, la confirmation s'ouvre.
              event.preventDefault()
              if (canStart()) setConfirmStart(true)
            }}
          >
            <section className="op-departure2__main">
              <span className="field-eyebrow">{today()}</span>
              <h1>Prêt à partir</h1>
              {operation ? (
                <p className={operation.stale ? 'op-operation op-operation--stale' : 'op-operation'} role="status">
                  Opération {operation.number} en cours depuis le {formatDateTime(operation.openedAt)}
                  {operation.stale ? ' — ouverte depuis plus de 24 h : vérifiez avec le bureau avant de partir.' : ''}
                </p>
              ) : null}
              <div className="op-departure2__cards">
                <div className="op-assignment">
                  <User aria-hidden="true" />
                  <div>
                    <span className="field-eyebrow">Opérateur</span>
                    <strong>{data.operator.name ?? auth.session?.displayName ?? '—'}</strong>
                  </div>
                </div>
                <div className="op-assignment">
                  <Tractor aria-hidden="true" />
                  <div>
                    <span className="field-eyebrow">Tracteur assigné</span>
                    <strong>
                      {data.equipment ? `${data.equipment.number ?? ''} ${data.equipment.name}`.trim() : 'Aucun'}
                    </strong>
                    <small>
                      {data.equipment
                        ? lastReading
                          ? `Dernier relevé : ${formatEngineHours(lastReading.value)} le ${formatDateTime(lastReading.recordedAt)}`
                          : 'Aucun relevé enregistré pour ce tracteur'
                        : ''}
                    </small>
                  </div>
                </div>
                <div className="op-assignment">
                  <MapPin aria-hidden="true" />
                  <div>
                    <span className="field-eyebrow">{hasPlan ? 'Route du départ' : 'Route assignée'}</span>
                    {hasPlan ? (
                      route ? (
                        <>
                          <strong className="op-route">
                            <span className="op-swatch" style={{ background: route.color ?? undefined }} aria-hidden="true" />
                            {route.name ?? 'Route'}
                          </strong>
                          <small>{routeActionLabel(route)}</small>
                        </>
                      ) : (
                        <strong>Aucune à faire</strong>
                      )
                    ) : data.route ? (
                      <strong className="op-route">
                        <span
                          className="op-swatch"
                          style={{ background: data.route.color ?? undefined }}
                          aria-hidden="true"
                        />
                        {data.route.name}
                      </strong>
                    ) : (
                      <strong>Aucune</strong>
                    )}
                  </div>
                </div>
                <div className="op-assignment">
                  <Home aria-hidden="true" />
                  <div>
                    <span className="field-eyebrow">Résidences</span>
                    <strong>
                      {hasPlan
                        ? route
                          ? routeResidences(route)
                          : '—'
                        : data.route
                          ? `${String(data.route.eligibleCount)} à faire`
                          : '—'}
                    </strong>
                  </div>
                </div>
              </div>
              {plan && hasPlan && (plan.routes.length > 1 || plan.routes.some((item) => item.action !== 'START')) ? (
                <RoutePlanList
                  plan={plan}
                  selectedId={route?.routeId ?? null}
                  disabled={pending}
                  onSelect={setPickedRouteId}
                />
              ) : null}
              <p className="op-muted">
                {hasPlan
                  ? 'Routes, ordre et tracteur fixés par le bureau. Une erreur d’affectation se corrige dans Gestion, pas ici.'
                  : 'Route et tracteur fixés par le bureau. Une erreur d’affectation se corrige dans Gestion, pas ici.'}
              </p>

              {blocked ? (
                <div className="op-blockers" role="alert">
                  <strong>
                    <TriangleAlert aria-hidden="true" /> Démarrage impossible
                  </strong>
                  <ul>
                    {noRouteToStart ? (
                      <li>
                        Aucune de vos routes n’est à faire dans l’opération en cours.{' '}
                        <em>Le bureau peut préparer un nouveau passage ou vous proposer du renfort.</em>
                      </li>
                    ) : null}
                    {blockers.map((blocker) => (
                      <li key={blocker}>
                        {blockerMessages[blocker].title} <em>{blockerMessages[blocker].fix}</em>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>

            <section className="op-departure2__side">
              <label className="op-field op-field--hours">
                <span>Compteur d’heures du tracteur au départ</span>
                <input
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder={lastReading ? formatEngineHours(lastReading.value).replace(' h', '') : 'ex. 1520,5'}
                  value={hours}
                  aria-invalid={(touched && !parsed.ok) || belowLast}
                  aria-describedby="op-hours-help"
                  onChange={(event) => {
                    setHours(event.target.value)
                  }}
                  onBlur={() => {
                    setTouched(true)
                  }}
                  disabled={blocked}
                />
                <small id="op-hours-help">
                  Heures moteur cumulées affichées au compteur (décimales permises), jamais des kilomètres. Un relevé
                  inférieur au dernier est refusé.
                </small>
              </label>
              {touched && !parsed.ok ? <p className="op-error">{engineHoursErrors[parsed.reason]}</p> : null}
              {belowLast ? (
                <p className="op-error" role="alert">
                  Ce relevé est inférieur au dernier relevé ({formatEngineHours(lastReading.value)}). Vérifiez le compteur;
                  s’il a été remplacé ou corrigé, le bureau doit l’enregistrer avant votre départ.
                </p>
              ) : null}
              {message ? (
                <p className="op-error" role="alert">
                  {message}
                </p>
              ) : null}

              <div className="op-departure2__info">
                {detectionAvailable ? (
                  <>
                    <strong>Détection automatique : en observation</strong>
                    <span>
                      La tablette peut proposer des fins de visite; seul le maintien de 3 s confirme le travail. Gardez
                      l’écran allumé et l’application ouverte.
                    </span>
                  </>
                ) : (
                  <>
                    <strong>Position pendant la sortie</strong>
                    <span>
                      La tablette enregistre sa position toutes les 30 secondes, application ouverte. Le navigateur
                      demandera l’autorisation de localisation; un refus n’empêche pas de travailler.
                    </span>
                  </>
                )}
              </div>
              <HoldButton
                className="field-hold--start op-departure2__go"
                holdMs={FIELD_MISSION_START_HOLD_MS}
                ariaLabel="Démarrer la mission — maintenir 2 secondes"
                idleLabel="Démarrer la mission"
                idleSub="maintenir 2 s"
                successLabel="Démarrage…"
                phase={pending ? 'confirming' : 'idle'}
                disabled={startDisabled}
                onConfirm={() => void start()}
                onCancel={() => {
                  setMessage('Trop court — maintenez 2 secondes pour démarrer.')
                }}
              />
              <button
                type="button"
                className="field-button field-button--outline"
                disabled={startDisabled || pending}
                onClick={() => {
                  if (canStart()) setConfirmStart(true)
                }}
              >
                Démarrer sans maintien
              </button>
            </section>
          </form>
        ) : null}

        {confirmStart && data ? (
          <div className="op-settings" role="dialog" aria-modal="true" aria-label="Confirmer le démarrage">
            <section className="op-settings__card">
              <h2>Démarrer la mission ?</h2>
              <p className="op-settings__note">
                {route ? `${route.name ?? 'Route'} (${routeActionLabel(route)})` : (data.route?.name ?? 'Route')} · {data.equipment ? `${data.equipment.number ?? ''} ${data.equipment.name}`.trim() : '—'}{' '}
                · compteur {parsed.ok ? formatEngineHours(parsed.value) : '—'}
              </p>
              <div className="op-confirm__actions">
                <button
                  type="button"
                  className="field-button field-button--outline field-button--tall"
                  onClick={() => {
                    setConfirmStart(false)
                  }}
                >
                  Annuler
                </button>
                <button type="button" className="op-start-button" disabled={pending} onClick={() => void start()}>
                  <Play aria-hidden="true" /> Confirmer le démarrage
                </button>
              </div>
            </section>
          </div>
        ) : null}

        {data && !data.openMission ? (
          <div className="op-start__aside">
            <HelpAvailability />
            <ReinforcementPanel missionId={null} />
            {activeOffers > 0 && offers.data?.missionId === null ? (
              <section className="op-reinforcement op-reinforcement--start" aria-label="Commencer le renfort">
                <h2>
                  <Handshake aria-hidden="true" /> Commencer le renfort
                </h2>
                <p className="op-muted">
                  Mission distincte dans l’opération en cours, avec votre tracteur assigné. Votre route n’est pas
                  occupée.
                </p>
                <label className="op-field">
                  <span>Compteur d’heures du tracteur (renfort)</span>
                  <input
                    className="op-input"
                    inputMode="decimal"
                    autoComplete="off"
                    value={reinforcementHours}
                    onChange={(event) => {
                      setReinforcementHours(event.target.value)
                    }}
                  />
                </label>
                {reinforcementMessage ? (
                  <p className="op-error" role="alert">
                    {reinforcementMessage}
                  </p>
                ) : null}
                <button
                  type="button"
                  className="field-button field-button--primary field-button--tall"
                  disabled={reinforcementPending}
                  onClick={() => void startReinforcement()}
                >
                  {reinforcementPending ? 'Démarrage…' : 'Commencer le renfort'}
                </button>
              </section>
            ) : null}
          </div>
        ) : null}
      </div>
    </main>
  )
}
